import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FamilyEvent, EventAttendee } from './useOptimizedEvents';
import { format, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';

interface CalendarData {
  events: FamilyEvent[];
  attendees: (EventAttendee & { profile: any })[];
  persons: any[];
}

/**
 * Hook optimisé qui charge toutes les données du calendrier en une seule requête RPC
 * Charge 3 mois de données (mois précédent, courant, suivant)
 */
export function useCalendarData(familyId?: string, currentDate?: Date) {
  const queryClient = useQueryClient();
  const baseDate = currentDate || new Date();
  
  // Charger 3 mois de données
  const startDate = startOfMonth(subMonths(baseDate, 1));
  const endDate = endOfMonth(addMonths(baseDate, 1));

  return useQuery({
    queryKey: ['calendar-data', familyId, format(startDate, 'yyyy-MM'), format(endDate, 'yyyy-MM')],
    queryFn: async (): Promise<CalendarData> => {
      if (!familyId) {
        return { events: [], attendees: [], persons: [] };
      }

      const { data, error } = await supabase.rpc('get_calendar_data', {
        p_family_id: familyId,
        p_start_date: format(startDate, 'yyyy-MM-dd'),
        p_end_date: format(endDate, 'yyyy-MM-dd')
      });

      if (error) {
        console.error('Error fetching calendar data:', error);
        throw error;
      }

      const result = (data as unknown) as CalendarData;

      // Pré-remplir les caches individuels
      if (result.events) {
        queryClient.setQueryData(['optimized-events', familyId], result.events);
      }
      if (result.persons) {
        queryClient.setQueryData(['optimized-person-profiles', familyId], result.persons);
      }

      return result;
    },
    enabled: !!familyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}
